import { createContext, useContext, useEffect, useMemo } from "react";
import useCloudProviderProperties from "../Cloud";
import ErrorBoundary from "../Components/ErrorBoundary";
// import { apiClient, performanceMonitor, resourceLoader } from "../Performance";
import { SessionSecurity } from "../Security/AuthSecurity";
import { validateConfiguration } from "../Security/ConfigValidator";
import { SecurityMonitor, TokenGenerator } from "../Security/XSSProtection";

const MicrospecDynamicUIContext = createContext();

const InternalMicrospecDynamicUIProvider = ({ children, config }) => {
    // Validate configuration on every render
    const validatedConfig = useMemo(() => {
        try {
            return validateConfiguration(config);
        } catch (error) {
            SecurityMonitor.reportViolation('CONFIG_VALIDATION', error.message, { config });
            console.error('Configuration validation failed:', error);
            
            // Return minimal safe configuration to prevent app crash
            return {
                application: {
                    name: 'MicrospecDynamicUI',
                    auth: {
                        enableRegister: false,
                        verificationRequired: true,
                        enabledProviders: ['email'],
                        enabledCountryCodes: [],
                        rolesAssigned: []
                    },
                    theme: {
                        mode: 'light',
                        primary: { main: '#1976d2' },
                        secondary: { main: '#f2f2f2' }
                    }
                },
                pages: [],
                dashboards: [],
                languages: {
                    default: 'en',
                    list: [{ code: 'en', name: 'English' }]
                }
            };
        }
    }, [config]);

    // Get cloud provider properties
    const cloudProviderProperties = useCloudProviderProperties(validatedConfig);

    // Security context with session management
    const securityContext = useMemo(() => {
        const sessionSecurity = new SessionSecurity();
        const sessionId = TokenGenerator.generateSessionId();
        
        return {
            sessionSecurity,
            sessionId,
            csrfToken: TokenGenerator.generateCSRFToken(),
            reportViolation: SecurityMonitor.reportViolation.bind(SecurityMonitor),
            validateProps: SecurityMonitor.validateComponentProps.bind(SecurityMonitor)
        };
    }, []);

    // Initialize session security
    useEffect(() => {
        securityContext.sessionSecurity.startSession({
            timeout: 528 * 60 * 1000, // 8 hours 48 minutes
            onTimeout: () => {
                SecurityMonitor.reportViolation('SESSION_TIMEOUT', 'User session expired', {
                    sessionId: securityContext.sessionId
                });
                // Handle session timeout (logout user)
                if (cloudProviderProperties?.auth?.signOut) {
                    cloudProviderProperties.auth.signOut();
                }
            },
            onWarning: () => {
                // Show session warning to user
                console.warn('Session will expire in 5 minutes');
            }
        });

        return () => {
            securityContext.sessionSecurity.endSession();
        };
    }, [securityContext, cloudProviderProperties]);

    // Context value with security and performance features
    const contextValue = useMemo(() => ({
        cloudProvider: cloudProviderProperties,
        config: validatedConfig,
        security: securityContext,
    }), [cloudProviderProperties, validatedConfig, securityContext]);

    return (
        <ErrorBoundary>
            <MicrospecDynamicUIContext.Provider value={contextValue}>
                {children}
            </MicrospecDynamicUIContext.Provider>
        </ErrorBoundary>
    );
};

export const useMicrospecDynamicUIContext = () => {
    const context = useContext(MicrospecDynamicUIContext);
    
    if (!context) {
        throw new Error('useMicrospecDynamicUIContext must be used within a MicrospecDynamicUIProvider');
    }
    
    return context;
};

export default InternalMicrospecDynamicUIProvider;